import { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Modal,
  Select,
  DatePicker,
  message,
  Tooltip,
  Drawer,
  Divider,
  List,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SendOutlined,
  DeleteOutlined,
  CopyOutlined,
  TeamOutlined,
  SearchOutlined,
  CloseCircleFilled,
  LeftOutlined,
  RightOutlined,
  BookOutlined,
  EyeOutlined,
  FileTextOutlined,
  PictureOutlined,
  ImportOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAuthStore } from "@/stores/authStore";
import { api } from "@/services/api";
import { API_ENDPOINTS, PAGE_SIZE, DEBOUNCE_DELAY } from "@/constants";
import { formatRelative } from "@/utils/date";
import type {
  ApiResponse,
  Deck,
  Card as DeckCard,
  TeacherStudentConnection,
} from "@/types";

interface AssignedStudent {
  studentId: string;
  studentName: string;
  assignmentStatus: string;
  hasProgress: boolean;
}
import type { Dayjs } from "dayjs";

const { Title, Text, Paragraph } = Typography;

type DeckStatus = "ALL" | "DRAFT" | "PUBLISHED" | "ARCHIVED";

const STATUS_TAG_COLORS: Record<string, string> = {
  DRAFT: "default",
  PUBLISHED: "green",
  ARCHIVED: "orange",
};

export default function DecksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { user } = useAuthStore();
  const isApproved = user?.teacherStatus === 'ACTIVE';

  const [statusFilter, setStatusFilter] = useState<DeckStatus>(
    (location.state as { statusFilter?: DeckStatus } | null)?.statusFilter ?? "ALL"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(
    undefined,
  );

  // View drawer state
  const [viewDeck, setViewDeck] = useState<Deck | null>(null);

  // Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignDeckId, setAssignDeckId] = useState<string | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [assignDeadline, setAssignDeadline] = useState<Dayjs | null>(null);

  // Debounce search
  const handleSearchChange = useCallback(
    (() => {
      let timer: ReturnType<typeof setTimeout>;
      return (value: string) => {
        setSearchTerm(value);
        clearTimeout(timer);
        timer = setTimeout(() => {
          setDebouncedSearch(value);
          setCursorHistory([]);
          setCurrentCursor(undefined);
        }, DEBOUNCE_DELAY);
      };
    })(),
    [],
  );

  const queryParams = useMemo(() => {
    const params: Record<string, string> = { limit: String(PAGE_SIZE) };
    if (statusFilter !== "ALL") params.status = statusFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    if (currentCursor) params.cursor = currentCursor;
    return params;
  }, [statusFilter, debouncedSearch, currentCursor]);

  const { data: decksResponse, isLoading } = useQuery({
    queryKey: ["teacher", "decks", queryParams],
    queryFn: () =>
      api.get<never, any>(API_ENDPOINTS.DECKS, { params: queryParams }),
  });

  const decks = (decksResponse?.data?.data ?? []) as Deck[];
  const hasMore = decksResponse?.data?.meta?.hasMore ?? false;
  const nextCursor = decksResponse?.data?.meta?.cursor;

  // Active deck count vs limit
  const { data: deckCountResponse } = useQuery({
    queryKey: ["teacher", "decks", "count"],
    queryFn: () => api.get<never, ApiResponse<{ count: number; max: number }>>(`${API_ENDPOINTS.DECKS}/count`),
    staleTime: 0,
  });
  const activeDeckCount = deckCountResponse?.data?.count ?? 0;
  const maxDecks = deckCountResponse?.data?.max ?? Infinity;
  const isAtDeckLimit = activeDeckCount >= maxDecks;

  // Students for assign modal
  const { data: studentsResponse } = useQuery({
    queryKey: ["teacher", "students"],
    queryFn: () =>
      api.get<never, ApiResponse<TeacherStudentConnection[]>>(
        API_ENDPOINTS.TEACHER_STUDENTS,
      ),
    enabled: assignModalOpen,
  });

  // Already-assigned students (with progress info) for the selected deck
  const { data: assignedStudentsResponse } = useQuery({
    queryKey: ["teacher", "deck-assigned-students", assignDeckId],
    queryFn: () =>
      api.get<never, ApiResponse<AssignedStudent[]>>(
        `${API_ENDPOINTS.DECKS}/${assignDeckId}/assignments`,
      ),
    enabled: !!assignDeckId,
  });

  const assignedStudents: AssignedStudent[] = assignedStudentsResponse?.data ?? [];
  const alreadyAssignedIds = new Set(assignedStudents.map((a) => a.studentId));

  const unassignMutation = useMutation({
    mutationFn: ({ deckId, studentId }: { deckId: string; studentId: string }) =>
      api.delete(`${API_ENDPOINTS.DECKS}/${deckId}/assignments/${studentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "deck-assigned-students", assignDeckId] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      message.success("Student unassigned from deck");
    },
    onError: () => message.error("Failed to unassign student"),
  });

  const handleUnassign = (student: AssignedStudent) => {
    if (!assignDeckId) return;
    Modal.confirm({
      title: student.hasProgress
        ? "⚠️ Student has progress — unassign anyway?"
        : `Unassign ${student.studentName}?`,
      content: student.hasProgress
        ? `${student.studentName} has already started this deck (status: ${student.assignmentStatus}). Unassigning will remove their assignment record. Their session history will remain but they won't see this deck anymore.`
        : `Remove ${student.studentName} from this deck? They haven't started yet, so no progress will be lost.`,
      okText: "Unassign",
      okButtonProps: { danger: true },
      onOk: () => unassignMutation.mutate({ deckId: assignDeckId, studentId: student.studentId }),
    });
  };

  const activeStudents = useMemo(
    () => (studentsResponse?.data ?? []).filter(
      (c) => c.status === "ACTIVE" && c.student?.status === "ACTIVE",
    ),
    [studentsResponse],
  );

  // Cards query for view drawer
  const { data: deckCardsResponse, isLoading: cardsLoading } = useQuery({
    queryKey: ["teacher", "deck-cards", viewDeck?.id],
    queryFn: () =>
      api.get<never, ApiResponse<DeckCard[]>>(
        `${API_ENDPOINTS.DECKS}/${viewDeck!.id}/cards`,
      ),
    enabled: !!viewDeck,
  });

  const deckCards = (deckCardsResponse?.data ?? []) as DeckCard[];

  // Mutations
  const publishMutation = useMutation({
    mutationFn: (deckId: string) =>
      api.post<never, ApiResponse<Deck>>(
        `${API_ENDPOINTS.DECKS}/${deckId}/publish`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      message.success("Deck published successfully");
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? "Failed to publish deck");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (deckId: string) =>
      api.delete<never, ApiResponse<void>>(`${API_ENDPOINTS.DECKS}/${deckId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks", "count"] });
      message.success("Deck archived successfully");
    },
    onError: () => {
      message.error("Failed to archive deck");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (deckId: string) =>
      api.post<never, ApiResponse<Deck>>(
        `${API_ENDPOINTS.DECKS}/${deckId}/duplicate`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks", "count"] });
      message.success("Deck duplicated successfully");
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? "Failed to duplicate deck");
    },
  });

  const assignMutation = useMutation({
    mutationFn: (payload: {
      deckId: string;
      studentIds: string[];
      deadline?: string;
    }) =>
      api.post<never, ApiResponse<{ assigned: number; alreadyAssigned: number }>>(
        `${API_ENDPOINTS.DECKS}/${payload.deckId}/assign`,
        {
          studentIds: payload.studentIds,
          deadline: payload.deadline,
        },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      const { assigned = 0, alreadyAssigned = 0 } = res.data ?? {};
      if (alreadyAssigned > 0 && assigned === 0) {
        message.warning(
          `This deck is already assigned to ${alreadyAssigned} student${alreadyAssigned > 1 ? 's' : ''}. No new assignments were made.`,
        );
      } else if (alreadyAssigned > 0) {
        message.info(
          `Deck assigned to ${assigned} student${assigned > 1 ? 's' : ''}. ${alreadyAssigned} already had this deck.`,
        );
      } else {
        message.success(`Deck assigned to ${assigned} student${assigned > 1 ? 's' : ''}`);
      }
      closeAssignModal();
    },
    onError: () => {
      message.error("Failed to assign deck");
    },
  });

  const openAssignModal = (deckId: string) => {
    setAssignDeckId(deckId);
    setSelectedStudentIds([]);
    setAssignDeadline(null);
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignDeckId(null);
    setSelectedStudentIds([]);
    setAssignDeadline(null);
  };

  const handleAssignSubmit = () => {
    if (!assignDeckId || selectedStudentIds.length === 0) return;

    const duplicates = selectedStudentIds.filter((id) =>
      alreadyAssignedIds.has(id),
    );

    const doAssign = () =>
      assignMutation.mutate({
        deckId: assignDeckId,
        studentIds: selectedStudentIds,
        deadline: assignDeadline?.toISOString(),
      });

    if (duplicates.length > 0) {
      Modal.confirm({
        title: "Already Assigned",
        content: `This deck is already assigned to this student. Do you want to proceed anyway?`,
        okText: "Assign Anyway",
        cancelText: "Cancel",
        onOk: doAssign,
      });
    } else {
      doAssign();
    }
  };

  const handleNextPage = () => {
    if (nextCursor) {
      setCursorHistory((prev) => [...prev, currentCursor ?? ""]);
      setCurrentCursor(nextCursor);
    }
  };

  const handlePrevPage = () => {
    setCursorHistory((prev) => {
      const copy = [...prev];
      const prevCursor = copy.pop();
      setCurrentCursor(prevCursor || undefined);
      return copy;
    });
  };

  const handleStatusChange = (value: string | number) => {
    setStatusFilter(value as DeckStatus);
    setCursorHistory([]);
    setCurrentCursor(undefined);
  };

  const handleArchiveConfirm = (deckId: string) => {
    Modal.confirm({
      title: "Archive Deck",
      content:
        "Are you sure you want to archive this deck? Students will no longer see it.",
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: () => archiveMutation.mutate(deckId),
    });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          My Decks
        </Title>
        <Space>
          <Tooltip title={isAtDeckLimit ? `You've reached the limit of ${maxDecks} decks` : undefined}>
            <Button
              icon={<ImportOutlined />}
              disabled={isAtDeckLimit}
              onClick={() => navigate("/teacher/decks/import")}
            >
              Import from ChatGPT
            </Button>
          </Tooltip>
          <Tooltip title={isAtDeckLimit ? `You've reached the limit of ${maxDecks} decks` : undefined}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={isAtDeckLimit}
              onClick={() => navigate("/teacher/decks/new")}
            >
              Create Deck
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Space
        direction="vertical"
        style={{ width: "100%", marginBottom: 20 }}
        size="middle"
      >
        <div
          style={{
            display: "inline-flex",
            gap: 8,
            padding: "6px 8px",
          }}
        >
          {(
            [
              {
                key: "ALL",
                label: "All",
                color: "#1677ff",
                bg: "#e6f4ff",
                border: "#91caff",
              },
              {
                key: "DRAFT",
                label: "Draft",
                color: "#d46b08",
                bg: "#fff7e6",
                border: "#ffd591",
              },
              {
                key: "PUBLISHED",
                label: "Published",
                color: "#389e0d",
                bg: "#f6ffed",
                border: "#b7eb8f",
              },
              {
                key: "ARCHIVED",
                label: "Archived",
                color: "#595959",
                bg: "#f5f5f5",
                border: "#d9d9d9",
              },
            ] as const
          ).map(({ key, label, color, bg, border }) => {
            const isActive = statusFilter === key;
            return (
              <button
                key={key}
                onClick={() => handleStatusChange(key)}
                style={{
                  padding: "6px 18px",
                  borderRadius: 8,
                  border: isActive
                    ? `1.5px solid ${border}`
                    : "1.5px solid transparent",
                  background: isActive
                    ? `linear-gradient(135deg, ${bg} 0%, #fff 100%)`
                    : "transparent",
                  color: isActive ? color : "#8c8c8c",
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: isActive ? `0 2px 8px ${border}80` : "none",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "#fff";
                    e.currentTarget.style.color = color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#8c8c8c";
                  }
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <Input
          placeholder="Search by topic name..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          suffix={
            searchTerm ? (
              <CloseCircleFilled
                style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }}
                onClick={() => handleSearchChange("")}
              />
            ) : null
          }
          style={{ maxWidth: isMobile ? "100%" : 360 }}
        />
      </Space>

      {isLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}
        >
          <Spin size="large" />
        </div>
      ) : decks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            debouncedSearch
              ? `No decks found matching "${debouncedSearch}"`
              : statusFilter !== "ALL"
                ? `No ${statusFilter.toLowerCase()} decks found`
                : "No decks yet. Create your first deck!"
          }
        >
          <Tooltip title={isAtDeckLimit ? `You've reached the limit of ${maxDecks} decks` : undefined}>
            <Button type="primary" disabled={isAtDeckLimit} onClick={() => navigate("/teacher/decks/new")}>
              Create Deck
            </Button>
          </Tooltip>
        </Empty>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {decks.map((deck) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={deck.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: 12,
                    height: "100%",
                    cursor: "pointer",
                    borderColor:
                      viewDeck?.id === deck.id ? "#6366f1" : undefined,
                    boxShadow:
                      viewDeck?.id === deck.id
                        ? "0 0 0 2px #ede9fe, 0 4px 16px rgba(99,102,241,0.15)"
                        : undefined,
                    transition: "border-color 0.2s, box-shadow 0.2s",
                  }}
                  onClick={() => setViewDeck(viewDeck?.id === deck.id ? null : deck)}
                  actions={buildDeckActions(deck)}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Tag color={STATUS_TAG_COLORS[deck.status]}>
                      {deck.status}
                    </Tag>
                  </div>
                  <Title
                    level={5}
                    style={{ marginBottom: 4 }}
                    ellipsis={{ rows: 2 }}
                  >
                    {deck.topicName}
                  </Title>
                  <Paragraph
                    type="secondary"
                    ellipsis={deck.description ? { rows: 2 } : false}
                    style={{
                      fontSize: 13,
                      marginBottom: 12,
                      minHeight: "2.6em",
                    }}
                  >
                    {deck.description ?? ""}
                  </Paragraph>
                  <Space size={[0, 8]} wrap>
                    <Tag icon={<BookOutlined />}>
                      {deck.cardCount} card{deck.cardCount !== 1 ? "s" : ""}
                    </Tag>
                    {deck._count?.assignments !== undefined && (
                      <Tag icon={<TeamOutlined />}>
                        {deck._count.assignments} assigned
                      </Tag>
                    )}
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Created {formatRelative(deck.createdAt)}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 8,
              marginTop: 24,
            }}
          >
            <Button
              icon={<LeftOutlined />}
              disabled={cursorHistory.length === 0}
              onClick={handlePrevPage}
            >
              Previous
            </Button>
            <Button disabled={!hasMore} onClick={handleNextPage}>
              Next <RightOutlined />
            </Button>
          </div>
        </>
      )}

      {/* Deck View Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 600 }}>
              {viewDeck?.topicName ?? "Deck Preview"}
            </span>
            {viewDeck && (
              <Tag
                color={STATUS_TAG_COLORS[viewDeck.status]}
                style={{ marginLeft: 4 }}
              >
                {viewDeck.status}
              </Tag>
            )}
          </div>
        }
        open={!!viewDeck}
        onClose={() => setViewDeck(null)}
        width={480}
        styles={{ body: { padding: "16px 24px" } }}
      >
        {viewDeck && (
          <>
            {/* Deck meta */}
            <div
              style={{
                background: "#f8f7ff",
                border: "1px solid #e0e7ff",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 20,
              }}
            >
              {viewDeck.subject && (
                <div style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Subject
                  </Text>
                  <div style={{ fontWeight: 500 }}>{viewDeck.subject.name}</div>
                </div>
              )}
              {viewDeck.chapter && (
                <div style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Chapter
                  </Text>
                  <div style={{ fontWeight: 500 }}>{viewDeck.chapter.name}</div>
                </div>
              )}
              {viewDeck.description && (
                <div style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Description
                  </Text>
                  <div>{viewDeck.description}</div>
                </div>
              )}
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Cards
                  </Text>
                  <div style={{ fontWeight: 600 }}>{viewDeck.cardCount}</div>
                </div>

                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Created
                  </Text>
                  <div style={{ fontWeight: 600 }}>
                    {formatRelative(viewDeck.createdAt)}
                  </div>
                </div>
              </div>
            </div>

            <Divider
              orientation="left"
              style={{ fontSize: 13, margin: "0 0 12px" }}
            >
              Cards ({deckCards.length})
            </Divider>

            {cardsLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 32,
                }}
              >
                <Spin />
              </div>
            ) : deckCards.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No cards in this deck yet"
                style={{ padding: "32px 0" }}
              />
            ) : (
              <List
                dataSource={deckCards}
                renderItem={(card, idx) => (
                  <List.Item
                    style={{
                      padding: "10px 14px",
                      marginBottom: 8,
                      background: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            background: "#6366f1",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                          {card.title}
                        </span>
                        {card.imageUrl ? (
                          <PictureOutlined
                            style={{ color: "#6366f1", marginLeft: "auto" }}
                          />
                        ) : (
                          <FileTextOutlined
                            style={{ color: "#8c8c8c", marginLeft: "auto" }}
                          />
                        )}
                      </div>
                      {card.frontText && (
                        <Text
                          type="secondary"
                          style={{ fontSize: 13, paddingLeft: 30 }}
                        >
                          {card.frontText}
                        </Text>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}

            <Divider style={{ margin: "16px 0" }} />
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              {viewDeck.status === "ARCHIVED" ? (
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    setViewDeck(null);
                    duplicateMutation.mutate(viewDeck.id);
                  }}
                >
                  Duplicate
                </Button>
              ) : (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    setViewDeck(null);
                    navigate(`/teacher/decks/${viewDeck.id}/edit`);
                  }}
                >
                  Edit Deck
                </Button>
              )}
              {viewDeck.status === "PUBLISHED" && (
                <Button
                  type="primary"
                  icon={<TeamOutlined />}
                  onClick={() => {
                    setViewDeck(null);
                    openAssignModal(viewDeck.id);
                  }}
                >
                  Assign
                </Button>
              )}
            </div>
          </>
        )}
      </Drawer>

      <Modal
        title="Assign Deck to Students"
        open={assignModalOpen}
        onCancel={closeAssignModal}
        onOk={handleAssignSubmit}
        okText="Assign"
        okButtonProps={{
          disabled: selectedStudentIds.length === 0,
          loading: assignMutation.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              Assign to New Students
            </Text>
            {(() => {
              const SELECT_ALL = "__select_all__";
              const unassigned = activeStudents.filter(
                (conn) => !alreadyAssignedIds.has(conn.studentId),
              );
              const allIds = unassigned.map((conn) => conn.studentId);
              const allSelected = allIds.length > 0 && allIds.every((id) => selectedStudentIds.includes(id));

              return (
                <Select
                  mode="multiple"
                  placeholder="Choose students to assign..."
                  style={{ width: "100%" }}
                  value={selectedStudentIds}
                  onChange={(values: string[]) => {
                    if (values.includes(SELECT_ALL)) {
                      setSelectedStudentIds(allIds);
                    } else {
                      setSelectedStudentIds(values);
                    }
                  }}
                  optionFilterProp="label"
                  allowClear
                  maxTagCount="responsive"
                  options={[
                    ...(unassigned.length > 1
                      ? [{
                          value: SELECT_ALL,
                          label: allSelected
                            ? `✓ All ${unassigned.length} students selected`
                            : `Select All (${unassigned.length})`,
                          style: { fontWeight: 600, color: allSelected ? "#16a34a" : "#6366f1" },
                        }]
                      : []),
                    ...unassigned.map((conn) => ({
                      value: conn.studentId,
                      label: conn.student?.name ?? conn.studentId,
                    })),
                  ]}
                />
              );
            })()}
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              Deadline (optional)
            </Text>
            <DatePicker
              style={{ width: "100%" }}
              value={assignDeadline}
              onChange={setAssignDeadline}
              disabledDate={(current) =>
                current && current.valueOf() < Date.now()
              }
            />
          </div>
          {assignedStudents.length > 0 && (
            <>
              <Divider style={{ margin: "4px 0" }} />
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Currently Assigned ({assignedStudents.length})
                </Text>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {assignedStudents.map((s) => (
                    <div
                      key={s.studentId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        borderRadius: 8,
                        background: s.hasProgress ? "#fff7ed" : "#f8fafc",
                        border: `1px solid ${s.hasProgress ? "#fed7aa" : "#e2e8f0"}`,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>{s.studentName}</Text>
                        <div>
                          <Tag
                            color={s.assignmentStatus === "COMPLETED" ? "green" : s.assignmentStatus === "IN_PROGRESS" ? "blue" : "default"}
                            style={{ fontSize: 11, marginTop: 2 }}
                          >
                            {s.assignmentStatus.replace("_", " ")}
                          </Tag>
                          {s.hasProgress && (
                            <Text type="secondary" style={{ fontSize: 11 }}>⚠ has progress</Text>
                          )}
                        </div>
                      </div>
                      <Button
                        size="small"
                        danger
                        type="text"
                        onClick={() => handleUnassign(s)}
                        loading={unassignMutation.isPending}
                      >
                        Unassign
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Space>
      </Modal>
    </div>
  );

  function buildDeckActions(deck: Deck): React.ReactNode[] {
    const isViewing = viewDeck?.id === deck.id;
    const actions: React.ReactNode[] = [
      <Tooltip title="View" key="view">
        <EyeOutlined
          onClick={(e) => { e.stopPropagation(); setViewDeck(isViewing ? null : deck); }}
          style={{
            color: isViewing ? "#6366f1" : undefined,
            background: isViewing ? "#ede9fe" : undefined,
            borderRadius: "50%",
            padding: isViewing ? 4 : undefined,
            transition: "all 0.2s",
          }}
        />
      </Tooltip>,
    ];

    if (deck.status !== "ARCHIVED") {
      actions.push(
        <Tooltip title="Edit" key="edit">
          <EditOutlined
            onClick={(e) => { e.stopPropagation(); navigate(`/teacher/decks/${deck.id}/edit`); }}
          />
        </Tooltip>,
      );
    }

    if (deck.status === "DRAFT") {
      const canPublish = deck.cardCount >= 4 && isApproved;
      const publishTooltip = !isApproved
        ? "Your account is pending admin approval"
        : deck.cardCount < 4
        ? "Minimum 4 cards required to publish"
        : "Publish";
      actions.push(
        <Tooltip title={publishTooltip} key="publish">
          <SendOutlined
            style={{
              color: canPublish ? "#16a34a" : "#d9d9d9",
              cursor: canPublish ? "pointer" : "not-allowed",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (canPublish) publishMutation.mutate(deck.id);
            }}
          />
        </Tooltip>,
      );
    }

    if (deck.status === "PUBLISHED") {
      actions.push(
        <Tooltip title="Assign" key="assign">
          <TeamOutlined
            onClick={(e) => { e.stopPropagation(); openAssignModal(deck.id); }}
            style={{ color: "#2563eb" }}
          />
        </Tooltip>,
      );
    }

    actions.push(
      <Tooltip title="Duplicate" key="duplicate">
        <CopyOutlined onClick={(e) => { e.stopPropagation(); duplicateMutation.mutate(deck.id); }} />
      </Tooltip>,
    );

    if (deck.status !== "ARCHIVED") {
      actions.push(
        <Tooltip title="Archive" key="archive">
          <DeleteOutlined
            onClick={(e) => { e.stopPropagation(); handleArchiveConfirm(deck.id); }}
            style={{ color: "#ef4444" }}
          />
        </Tooltip>,
      );
    }

    return actions;
  }
}
